import { PropsWithChildren, ReactNode } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { AtlassianLogo } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { LayoutVersion } from "./product-overlay-template";

export const Modal = ({
    children,
    showBlueHeaderOnMobile,
    showBrandFooter,
    showProductLogoOnLargeScreens,
    size,
    version,
    productLogo,
    moveLogoToModal
}: PropsWithChildren<{
    showBlueHeaderOnMobile?: boolean;
    showProductLogoOnLargeScreens?: boolean;
    size?: "sm" | "lg";
    showBrandFooter?: boolean;
    version?: LayoutVersion;
    productLogo?: ReactNode;
    moveLogoToModal?: boolean;
    // eslint-disable-next-line complexity
}>) => {
    const { formatMessage } = useIntl();
    const logoLabel = formatMessage({
        defaultMessage: "Atlassian logo",
        id: "signup.layouts.modal.atlassianLogoLabel"
    });
    const team26ModalLayout = checkFeatureFlagGate(
        featureFlags.cfind4831ProductOverlayModalVisualUpdates
    );
    // the `version` prop will be removed when cfind4831ProductOverlayModalVisualUpdates is productionized
    version = team26ModalLayout ? LayoutVersion.V3 : version;
    return (
        <div
            css={[
                positioningCss,
                !moveLogoToModal && positioningCssWithGap,
                showBlueHeaderOnMobile && blueHeaderOnMobileCss,
                version === LayoutVersion.V3 && LayoutV3.positioningCss,
                version === LayoutVersion.V3 &&
                    showProductLogoOnLargeScreens &&
                    noPositioningTopPaddingCss
            ]}
            data-testid="modal"
        >
            {productLogo && moveLogoToModal && (
                <div
                    css={[
                        logoWrapper,
                        !showProductLogoOnLargeScreens && hideAboveSmCss
                    ]}
                >
                    {productLogo}
                </div>
            )}
            <div
                css={[
                    modalCss,
                    size === "sm" && smModalCss,
                    size === "lg" && lgModalCss,
                    version === LayoutVersion.V2 && LayoutV2.modalCss,
                    version === LayoutVersion.V3 && LayoutV3.modalCss
                ]}
            >
                {children}
            </div>
            {showBrandFooter && (
                <div css={[moveLogoToModal && footerCss]}>
                    <div css={hideAboveSmCss} aria-hidden="true">
                        <AtlassianLogo
                            appearance="brand"
                            size="small"
                            testId="atlassian-logo-sm-screen"
                            label={logoLabel}
                        />
                    </div>
                    <div css={lgVisibleCss} aria-hidden="true">
                        <AtlassianLogo
                            appearance="inverse"
                            size="small"
                            testId="atlassian-logo-lg-screen"
                            label={logoLabel}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const blueHeaderOnMobileCss = css({
    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 1440 518' preserveAspectRatio='none' fill='%23E9F2FE' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1331.5 -20.8369L-38.614 -36.9811L-43.579 433.588H243.576L248.105 518L1598.49 435.131L1574.37 -14.3789L1331.5 0.525597V-20.8369Z'/%3E%3C/svg%3E%0A")`,
    backgroundSize: "100% 120px",
    backgroundPosition: "center 0",
    backgroundRepeat: "no-repeat",
    paddingBlockStart: 42,
    [media.above.sm]: {
        backgroundImage: "none"
    }
});

const hideAboveSmCss = css({
    [media.above.sm]: {
        display: "none !important"
    }
});

const lgVisibleCss = css({
    display: "none",
    [media.above.sm]: {
        display: "block !important"
    }
});

const modalCss = css({
    overflow: "hidden",
    transition: "width 0.5s ease-in-out",
    maxWidth: "100%",
    [media.above.sm]: {
        backgroundColor: token("elevation.surface"),
        borderRadius: 8,
        boxShadow: token("elevation.shadow.overlay"),
        minHeight: 400
    }
});

const LayoutV2 = {
    modalCss: css({
        [media.above.sm]: {
            borderRadius: 3
        }
    })
};

const LayoutV3 = {
    modalCss: css({
        [media.above.sm]: {
            borderRadius: 12 // todo replace with radius.xlarge token when available
        }
    }),
    positioningCss: css({
        [media.above.sm]: {
            justifyContent: "flex-start",
            padding: token("space.1000")
        }
    })
};

const smModalCss = css({
    width: 560
});

const lgModalCss = css({
    width: 1140
});

const positioningCss = css({
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    padding: 24,
    [media.above.sm]: {
        justifyContent: "center",
        minHeight: "100vh",
        padding: 32,
        ["@supports (min-height: 100dvh)"]: {
            minHeight: "100dvh"
        }
    }
});

const footerCss = css({
    marginBlockStart: token("space.400")
});

const positioningCssWithGap = css({
    gap: token("space.400")
});

const logoWrapper = css({
    display: "flex",
    justifyContent: "center",
    margin: `${token("space.100")} ${token("space.0")} ${token("space.200")}`,
    [media.above.sm]: {
        color: "white",
        marginBlockStart: token("space.0")
    }
});

const noPositioningTopPaddingCss = css({
    paddingBlockStart: token("space.300"),
    [media.above.sm]: {
        paddingBlockStart: token("space.500")
    }
});
